// components/admin/ProductForm.js
import React, { useState, useEffect } from "react"
import {
  fetchMaterials,
  fetchMaterialLinks,
  fetchProductStructure,
  fetchProducts,
  fetchFullProductStructure,
} from "../../api"
import { materialManager } from "../../materialManager"
import TypeLinksComponent from "./TypeLinksComponent"

const ProductForm = ({
  product,
  category,
  family,
  isEditing,
  familyInitialData,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    slug: "",
    onpage_code: "",
    price: 0,
    tag: "",
    typeLinks: [],
    materials: {},
    lang: { it: "", en: "", fr: "", de: "" },
    usageImages: ["", "", "", ""],
    relatedProducts: [],
  })

  // Material management state
  const [allMaterials, setAllMaterials] = useState([])
  const [customTypes, setCustomTypes] = useState([])
  const [familyDefaults, setFamilyDefaults] = useState({})
  const [inheritFromFamily, setInheritFromFamily] = useState(true)
  const [activeType, setActiveType] = useState(null)
  const [view, setView] = useState("general") // general, types, typeLinks, infoSection
  const [availableCategories, setAvailableCategories] = useState([])
  const [availableFamilies, setAvailableFamilies] = useState([])
  const [allProducts, setAllProducts] = useState([]) // For related products selector
  const [selectedCategoryForMove, setSelectedCategoryForMove] = useState("")
  const [selectedFamilyForMove, setSelectedFamilyForMove] = useState("")

  // UI state
  const [loading, setLoading] = useState(false)
  const [showTypeForm, setShowTypeForm] = useState(false)
  const [editingTypeIndex, setEditingTypeIndex] = useState(null)

  // Type form data
  const [typeFormData, setTypeFormData] = useState({
    technicalName: "",
    displayLabel: "",
    materials: [],
    standardMaterials: [],
    optionalMaterials: [],
  })

  // Tags options for dropdown
  const tagOptions = ["", "bestseller", "new", "sale", "premium"]

  // Initialize form data if editing
  useEffect(() => {
    const initializeForm = async () => {
      setLoading(true)
      await loadMaterials() // Assicura che i materiali siano caricati prima di processare

      if (familyInitialData && familyInitialData.defaultMaterials) {
        setFamilyDefaults(familyInitialData.defaultMaterials)
        const typesFromFamily = Object.entries(
          familyInitialData.defaultMaterials
        ).map(([technicalName, config]) => {
          const standardNames = (config.standard || []).map(
            (code) =>
              materialManager.findMaterialBySlug(code.trim())?.name || code
          )
          const optionalNames = (config.optional || []).map(
            (code) =>
              materialManager.findMaterialBySlug(code.trim())?.name || code
          )
          return {
            technicalName,
            displayLabel: config.label || technicalName,
            materials: [...new Set([...standardNames, ...optionalNames])],
            standardMaterials: standardNames,
            optionalMaterials: optionalNames,
          }
        })
        setCustomTypes(typesFromFamily)
        if (typesFromFamily.length > 0) {
          setActiveType(typesFromFamily[0].technicalName)
        }
      }

      if (isEditing && product) {
        setFormData({
          // PRESERVA TUTTI i campi del prodotto originale
          ...product,
          // Assicurati che i campi del form siano inizializzati correttamente
          name: product.name || "",
          code: product.code || "",
          slug: product.slug || "",
          projectCode: product.projectCode || "",
          onpage_code: product.onpage_code || product.onpagecode || "", // Entrambe le varianti
          price: product.price || 0,
          tag: product.tag || "",
          typeLinks: product.typeLinks || [],
          materials: product.materials || {},
          lang: product.lang || { it: "", en: "", fr: "", de: "" },
          // PRESERVA ESPLICITAMENTE i campi che potrebbero essere persi
          customViews: product.customViews || [],
          materialLinks: product.materialLinks || [],
          usageImages: product.usageImages || ["", "", "", ""],
          relatedProducts: product.relatedProducts || [],
          // Altri eventuali campi del prodotto
        })

        // Resto del codice per materials e customTypes rimane uguale
        if (product.materials && Object.keys(product.materials).length > 0) {
          setInheritFromFamily(false)
          const typesFromProduct = Object.entries(product.materials).map(
            ([technicalName, config]) => {
              const standardNames = (config.standard || []).map(
                (code) =>
                  materialManager.findMaterialBySlug(code.trim())?.name || code
              )
              const optionalNames = (config.optional || []).map(
                (code) =>
                  materialManager.findMaterialBySlug(code.trim())?.name || code
              )
              return {
                technicalName,
                displayLabel: config.label || technicalName,
                materials: [...new Set([...standardNames, ...optionalNames])],
                standardMaterials: standardNames,
                optionalMaterials: optionalNames,
              }
            }
          )
          setCustomTypes(typesFromProduct)
          if (typesFromProduct.length > 0) {
            setActiveType(typesFromProduct[0].technicalName)
          }
        }
      }
      setLoading(false)
    }
    initializeForm()
  }, [isEditing, product, familyInitialData])

  // Generate slug automatically from name
  useEffect(() => {
    if (!isEditing && formData.name && !formData.slug) {
      const generatedSlug = formData.name
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .trim()

      setFormData((prev) => ({
        ...prev,
        slug: generatedSlug,
      }))
    }
  }, [formData.name, isEditing, formData.slug])

  useEffect(() => {
    loadAvailableStructure()
    loadAllProducts()
  }, [])

  // Load all products for related products selector
  const loadAllProducts = async () => {
    try {
      // Load both flat products and full structure to combine them
      const [products, fullStructure] = await Promise.all([
        fetchProducts(),
        fetchFullProductStructure(),
      ])

      // Build a map of family defaults from the full structure
      const familyDefaultsMap = {}
      if (fullStructure && fullStructure.categories) {
        Object.entries(fullStructure.categories).forEach(([categoryName, categoryData]) => {
          if (categoryData.families) {
            Object.entries(categoryData.families).forEach(([familyName, familyData]) => {
              familyDefaultsMap[familyName] = {
                defaultMaterials: familyData.defaultMaterials || {},
                defaultMaterialLinks: familyData.defaultMaterialLinks || [],
                typeLinks: familyData.typeLinks || [],
                collectionTranslations: familyData.collectionTranslations || [],
              }
            })
          }
        })
      }

      // Enhance flat products with family defaults
      const productsWithDefaults = products.map((product) => {
        // Use existing _family if present, otherwise use family field (without underscore)
        const familyName = product._family || product.family
        const categoryName = product._category || product.category

        return {
          ...product,
          _family: familyName,
          _category: categoryName,
          _familyDefaults: familyName ? (familyDefaultsMap[familyName] || {}) : {},
        }
      })

      console.log("Family defaults map:", familyDefaultsMap)
      console.log("Products with defaults sample:", productsWithDefaults[0])

      setAllProducts(productsWithDefaults)
    } catch (error) {
      console.error("Error loading products:", error)
    }
  }

  // Handle usage image change
  const handleUsageImageChange = (index, value) => {
    setFormData((prev) => {
      const newImages = [...(prev.usageImages || ["", "", ""])]
      newImages[index] = value
      return { ...prev, usageImages: newImages }
    })
  }

  // Handle related product change
  const handleRelatedProductChange = (index, productCode) => {
    setFormData((prev) => {
      const newRelated = [...(prev.relatedProducts || [])]
      if (productCode) {
        // Find existing or create new entry
        if (newRelated[index]) {
          newRelated[index] = { ...newRelated[index], code: productCode }
        } else {
          newRelated[index] = { code: productCode, materials: {} }
        }
      } else {
        // Remove entry if no product selected
        newRelated.splice(index, 1)
      }
      return { ...prev, relatedProducts: newRelated }
    })
  }

  // Handle related product material change
  const handleRelatedProductMaterialChange = (productIndex, materialType, materialSlug) => {
    setFormData((prev) => {
      const newRelated = [...(prev.relatedProducts || [])]
      if (newRelated[productIndex]) {
        newRelated[productIndex] = {
          ...newRelated[productIndex],
          materials: {
            ...(newRelated[productIndex].materials || {}),
            [materialType]: materialSlug,
          },
        }
      }
      return { ...prev, relatedProducts: newRelated }
    })
  }

  // Handle related product image change
  const handleRelatedProductImageChange = (productIndex, imageUrl) => {
    setFormData((prev) => {
      const newRelated = [...(prev.relatedProducts || [])]
      if (newRelated[productIndex]) {
        newRelated[productIndex] = {
          ...newRelated[productIndex],
          imageUrl: imageUrl || "",
        }
      }
      return { ...prev, relatedProducts: newRelated }
    })
  }

  // Aggiungi questa funzione nel componente
  const loadAvailableStructure = async () => {
    try {
      const structure = await fetchProductStructure()
      setAvailableCategories(Object.keys(structure))

      // Crea una mappa di tutte le famiglie per categoria
      const allFamiliesMap = {}
      Object.keys(structure).forEach((cat) => {
        allFamiliesMap[cat] = structure[cat]
      })

      // Set initial values if editing
      if (isEditing && product) {
        setSelectedCategoryForMove(product._category || product.category || category)
        setSelectedFamilyForMove(product._family || product.family || family)

        // Load families for the current category
        const productCategory = product._category || product.category || category
        if (structure[productCategory]) {
          setAvailableFamilies(structure[productCategory])
        }
      } else {
        setSelectedCategoryForMove(category)
        setSelectedFamilyForMove(family)

        if (structure[category]) {
          setAvailableFamilies(structure[category])
        }
      }

      // Store all families structure for later use
      window.allFamiliesStructure = allFamiliesMap
    } catch (error) {
      console.error("Failed to load structure:", error)
    }
  }

  // Aggiungi questa funzione per gestire il cambio categoria
  const handleCategoryChange = async (newCategory) => {
    setSelectedCategoryForMove(newCategory)

    try {
      // Use stored structure instead of fetching again
      const allStructure = window.allFamiliesStructure
      if (allStructure && allStructure[newCategory]) {
        setAvailableFamilies(allStructure[newCategory])
        // Reset family selection when category changes
        setSelectedFamilyForMove("")
      } else {
        // Fallback: fetch structure if not available
        const structure = await fetchProductStructure()
        if (structure[newCategory]) {
          setAvailableFamilies(structure[newCategory])
          setSelectedFamilyForMove("")
        }
      }
    } catch (error) {
      console.error("Failed to load families:", error)
    }
  }

  // Load all materials
  const loadMaterials = async () => {
    try {
      const materialsData = await fetchMaterials()
      setAllMaterials(materialsData.materials || [])
      if (materialManager.allMaterials.length === 0) {
        await materialManager.initializeAllMaterials()
      }
    } catch (error) {
      console.error("Failed to load materials:", error)
    }
  }

  // Handle text input changes

  const handleChange = (e) => {
    const { name, value, type } = e.target
    const val = type === "number" ? (value === "" ? "" : Number(value)) : value
    setFormData((prev) => ({ ...prev, [name]: val }))
  }

  const handleLangChange = (e, langCode) => {
    const { value } = e.target
    setFormData((prev) => ({
      ...prev,
      lang: { ...prev.lang, [langCode]: value },
    }))
  }

  const handleInheritToggle = () => {
    setInheritFromFamily((prev) => !prev)
  }

  // Handle adding a new type
  const handleAddType = () => {
    setEditingTypeIndex(null)
    setTypeFormData({
      technicalName: "",
      displayLabel: "",
      materials: [],
      standardMaterials: [],
      optionalMaterials: [],
    })
    setShowTypeForm(true)
  }

  // Handle editing an existing type
  const handleEditType = (index) => {
    const type = customTypes[index]
    setEditingTypeIndex(index)
    setTypeFormData({
      technicalName: type.technicalName,
      displayLabel: type.displayLabel,
      materials: type.materials,
      standardMaterials: type.standardMaterials,
      optionalMaterials: type.optionalMaterials,
    })
    setShowTypeForm(true)
  }

  // Handle deleting a type
  const handleDeleteType = (index) => {
    if (!window.confirm("Are you sure you want to delete this type?")) return

    const updatedTypes = [...customTypes]
    updatedTypes.splice(index, 1)
    setCustomTypes(updatedTypes)

    // Update form data
    const updatedMaterials = { ...formData.materials }
    const deletedType = customTypes[index].technicalName
    delete updatedMaterials[deletedType]

    setFormData((prev) => ({
      ...prev,
      materials: updatedMaterials,
    }))

    // Set active type to first remaining type or null
    if (updatedTypes.length > 0) {
      setActiveType(updatedTypes[0].technicalName)
    } else {
      setActiveType(null)
    }
  }

  // Handle duplicating a type
  const handleDuplicateType = (index) => {
    const typeToDuplicate = customTypes[index]
    const newType = {
      ...typeToDuplicate,
      technicalName: `${typeToDuplicate.technicalName}_copy`,
      displayLabel: `${typeToDuplicate.displayLabel} (Copy)`,
    }

    setCustomTypes((prev) => [...prev, newType])

    // Update form data
    const updatedMaterials = { ...formData.materials }
    updatedMaterials[newType.technicalName] = {
      label: newType.displayLabel,
      standard: newType.standardMaterials.map((name) => {
        const material = materialManager.findMaterialByName(name)
        return material ? material.slug : materialManager.generateSlug(name)
      }),
      optional: newType.optionalMaterials.map((name) => {
        const material = materialManager.findMaterialByName(name)
        return material ? material.slug : materialManager.generateSlug(name)
      }),
    }

    setFormData((prev) => ({
      ...prev,
      materials: updatedMaterials,
    }))
  }

  // Handle type form field changes
  const handleTypeFormChange = (e) => {
    const { name, value } = e.target
    setTypeFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Handle material status change (standard, optional, or none)
  const handleMaterialStatusChange = (materialName, status) => {
    setTypeFormData((prev) => {
      const newState = { ...prev }

      // Remove from all statuses first
      newState.standardMaterials = newState.standardMaterials.filter(
        (m) => m !== materialName
      )
      newState.optionalMaterials = newState.optionalMaterials.filter(
        (m) => m !== materialName
      )

      // Add to the appropriate status
      if (status === "standard") {
        newState.standardMaterials.push(materialName)
      } else if (status === "optional") {
        newState.optionalMaterials.push(materialName)
      }

      // Make sure it's in the materials list if not already
      if (!newState.materials.includes(materialName)) {
        newState.materials.push(materialName)
      }

      return newState
    })
  }

  // Handle adding a material to the type
  const handleAddMaterial = (materialName) => {
    setTypeFormData((prev) => ({
      ...prev,
      materials: prev.materials.includes(materialName)
        ? prev.materials
        : [...prev.materials, materialName],
    }))
  }

  // Handle bulk add of materials
  const handleBulkAddMaterials = (
    materialsToAdd,
    asStandard = false,
    asOptional = false
  ) => {
    setTypeFormData((prev) => {
      const newMaterials = [...prev.materials]
      const newStandardMaterials = [...prev.standardMaterials]
      const newOptionalMaterials = [...prev.optionalMaterials]

      materialsToAdd.forEach((materialName) => {
        // Add to materials list if not already there
        if (!newMaterials.includes(materialName)) {
          newMaterials.push(materialName)
        }

        // Add to standard or optional based on parameters
        if (asStandard && !newStandardMaterials.includes(materialName)) {
          newStandardMaterials.push(materialName)
          // Remove from optional if it was there
          const optionalIndex = newOptionalMaterials.indexOf(materialName)
          if (optionalIndex > -1) {
            newOptionalMaterials.splice(optionalIndex, 1)
          }
        } else if (asOptional && !newOptionalMaterials.includes(materialName)) {
          newOptionalMaterials.push(materialName)
          // Remove from standard if it was there
          const standardIndex = newStandardMaterials.indexOf(materialName)
          if (standardIndex > -1) {
            newStandardMaterials.splice(standardIndex, 1)
          }
        }
      })

      return {
        ...prev,
        materials: newMaterials,
        standardMaterials: newStandardMaterials,
        optionalMaterials: newOptionalMaterials,
      }
    })
  }

  // Handle removing a material from the type
  const handleRemoveMaterial = (materialName) => {
    setTypeFormData((prev) => ({
      ...prev,
      materials: prev.materials.filter((name) => name !== materialName),
      standardMaterials: prev.standardMaterials.filter(
        (name) => name !== materialName
      ),
      optionalMaterials: prev.optionalMaterials.filter(
        (name) => name !== materialName
      ),
    }))
  }

  // Handle saving the type form
  const handleSaveType = () => {
    // Validate form
    if (!typeFormData.technicalName || !typeFormData.displayLabel) {
      alert("Technical name and display label are required")
      return
    }

    // Check for duplicate technical name when adding
    if (editingTypeIndex === null) {
      const exists = customTypes.some(
        (type) => type.technicalName === typeFormData.technicalName
      )
      if (exists) {
        alert("A type with this technical name already exists")
        return
      }
    }

    // Update custom types
    if (editingTypeIndex !== null) {
      // Update existing type
      const updatedTypes = [...customTypes]
      updatedTypes[editingTypeIndex] = { ...typeFormData }
      setCustomTypes(updatedTypes)

      // Update active type if it was the one being edited
      if (activeType === customTypes[editingTypeIndex].technicalName) {
        setActiveType(typeFormData.technicalName)
      }
    } else {
      // Add new type
      setCustomTypes((prev) => [...prev, { ...typeFormData }])
      setActiveType(typeFormData.technicalName)
    }

    // Update form data
    const updatedMaterials = { ...formData.materials }

    // If we're editing, remove the old technical name entry if it changed
    if (
      editingTypeIndex !== null &&
      customTypes[editingTypeIndex].technicalName !== typeFormData.technicalName
    ) {
      delete updatedMaterials[customTypes[editingTypeIndex].technicalName]
    }

    // Add/update the entry for this technical name
    updatedMaterials[typeFormData.technicalName] = {
      label: typeFormData.displayLabel,
      standard: typeFormData.standardMaterials.map((name) => {
        const material = materialManager.findMaterialByName(name)
        return material ? material.slug : materialManager.generateSlug(name)
      }),
      optional: typeFormData.optionalMaterials.map((name) => {
        const material = materialManager.findMaterialByName(name)
        return material ? material.slug : materialManager.generateSlug(name)
      }),
    }

    setFormData((prev) => ({
      ...prev,
      materials: updatedMaterials,
    }))

    // Close the form
    setShowTypeForm(false)
  }

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault()

    // PRESERVA TUTTI i dati originali del prodotto se è in editing
    const submitData = isEditing
      ? {
          // MANTIENI TUTTO dai dati originali
          ...product,
          // Sovrascrivi solo con le modifiche dal form
          ...formData,
          // IMPORTANTE: Aggiungi sempre categoria e famiglia (anche se non cambiate)
          category: selectedCategoryForMove || category,
          family: selectedFamilyForMove || family,
          // PRESERVA ESPLICITAMENTE i campi critici che potrebbero essere persi
          onpage_code: formData.onpage_code || product?.onpage_code || "",
          customViews: product?.customViews || [],
          materialLinks: product?.materialLinks || [],
        }
      : {
          // Per prodotti nuovi, usa solo formData
          ...formData,
          category: selectedCategoryForMove || category,
          family: selectedFamilyForMove || family,
          customViews: [],
          materialLinks: [],
        }

    // DEBUG: Vediamo cosa stiamo inviando
    console.log("🚀 Product submit data:", {
      originalCategory: category,
      originalFamily: family,
      newCategory: selectedCategoryForMove,
      newFamily: selectedFamilyForMove,
      willMove:
        selectedCategoryForMove !== category ||
        selectedFamilyForMove !== family,
      fullSubmitData: submitData,
      preservedFields: {
        onpage_code: submitData.onpage_code,
        customViews: submitData.customViews?.length || 0,
        materialLinks: submitData.materialLinks?.length || 0,
      },
    })

    // Resto della logica rimane uguale
    if (inheritFromFamily) {
      submitData.materials = {}
      submitData.typeLinks = []
    } else {
      const formattedMaterials = {}
      customTypes.forEach((type) => {
        formattedMaterials[type.technicalName] = {
          label: type.displayLabel,
          standard: type.standardMaterials.map(
            (name) => materialManager.nameToSlug(name) || name
          ),
          optional: type.optionalMaterials.map(
            (name) => materialManager.nameToSlug(name) || name
          ),
        }
      })
      submitData.materials = formattedMaterials
    }

    console.log("Final submit data being sent:", submitData)
    onSave(submitData)
  }

  // Get materials for a collection
  const getMaterialsByCollection = (collection) => {
    return allMaterials.filter((material) => material.collection === collection)
  }

  // Get unique collections
  const getUniqueCollections = () => {
    const collectionsSet = new Set()
    allMaterials.forEach((material) => {
      if (material.collection) {
        collectionsSet.add(material.collection)
      }
    })
    return Array.from(collectionsSet).sort()
  }

  return (
    <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
      <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-5xl ay-max-h-screen ay-overflow-auto ay-flex ay-flex-col">
        <h2 className="ay-text-xl ay-font-bold ay-mb-4">
          {isEditing ? "Edit Product" : "Add Product"} in {category} / {family}
        </h2>

        {/* Fixed bottom actions bar */}
        <div className="ay-sticky ay-bottom-0 ay-bg-white ay-border-t ay-border-gray-200 ay-mt-6 ay-p-4 ay-flex ay-justify-end ay-gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="ay-px-4 ay-py-2 ay-border ay-border-transparent ay-rounded-md ay-shadow-sm ay-bg-blue-500 ay-text-white ay-hover:ay-bg-blue-600"
          >
            {isEditing ? "Update" : "Create"}
          </button>
        </div>

        <form className="ay-flex-grow ay-overflow-auto ay-pb-16">
          {/* Navigation Tabs */}
          <div className="ay-border-b ay-border-gray-200 ay-mb-4">
            <nav className="ay-flex ay-space-x-4">
              <button
                type="button"
                onClick={() => setView("general")}
                className={`ay-py-2 ay-px-3 ay-text-sm ay-font-medium ${
                  view === "general"
                    ? "ay-border-b-2 ay-border-blue-500 ay-text-blue-600"
                    : "ay-text-gray-500 ay-hover:ay-text-gray-700"
                }`}
              >
                General Information
              </button>
              <button
                type="button"
                onClick={() => setView("types")}
                className={`ay-py-2 ay-px-3 ay-text-sm ay-font-medium ${
                  view === "types"
                    ? "ay-border-b-2 ay-border-blue-500 ay-text-blue-600"
                    : "ay-text-gray-500 ay-hover:ay-text-gray-700"
                }`}
              >
                Material Types
              </button>
              <button
                type="button"
                onClick={() => setView("typeLinks")}
                className={`ay-py-2 ay-px-3 ay-text-sm ay-font-medium ${
                  view === "typeLinks"
                    ? "ay-border-b-2 ay-border-blue-500 ay-text-blue-600"
                    : "ay-text-gray-500 ay-hover:ay-text-gray-700"
                }`}
              >
                Type Links
              </button>
              <button
                type="button"
                onClick={() => setView("infoSection")}
                className={`ay-py-2 ay-px-3 ay-text-sm ay-font-medium ${
                  view === "infoSection"
                    ? "ay-border-b-2 ay-border-blue-500 ay-text-blue-600"
                    : "ay-text-gray-500 ay-hover:ay-text-gray-700"
                }`}
              >
                Info Section
              </button>
            </nav>
          </div>

          {/* General Information View */}
          {view === "general" && (
            <div>
              <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-4 ay-mb-4">
                {/* Product Name */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    required
                  />
                </div>

                {/* Product Code */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Product Code *
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    required
                  />
                  {!isEditing && (
                    <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                      Unique identifier for the product.
                    </p>
                  )}
                </div>

                {/* Slug */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Slug
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                  />
                  <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                    URL-friendly identifier (auto-generated if empty).
                  </p>
                </div>

                {/* Onpage Code */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    OnPage Code
                  </label>
                  <input
                    type="text"
                    name="onpage_code"
                    value={formData.onpage_code}
                    onChange={handleChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                  />
                  <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                    Internal onpage_code project reference code.
                  </p>
                </div>

                {/* Price */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Price
                  </label>
                  <div className="ay-relative ay-rounded-md ay-shadow-sm">
                    <div className="ay-absolute ay-inset-y-0 ay-left-0 ay-pl-3 ay-flex ay-items-center ay-pointer-events-none">
                      <span className="ay-text-gray-500">€</span>
                    </div>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      className="ay-block ay-w-full ay-pl-7 ay-pr-12 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    />
                  </div>
                </div>

                {/* Tag */}
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Tag
                  </label>
                  <select
                    name="tag"
                    value={formData.tag}
                    onChange={handleChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                  >
                    {tagOptions.map((tag) => (
                      <option key={tag} value={tag}>
                        {tag
                          ? tag.charAt(0).toUpperCase() + tag.slice(1)
                          : "None"}
                      </option>
                    ))}
                  </select>
                </div>
                {isEditing && (
                  <>
                    {/* Category Selection */}
                    <div>
                      <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                        Category
                      </label>
                      <select
                        value={selectedCategoryForMove}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                      >
                        <option value="">Select Category</option>
                        {availableCategories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                      <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                        Change category to move this product
                      </p>
                    </div>

                    {/* Family Selection */}
                    <div>
                      <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                        Family
                      </label>
                      <select
                        value={selectedFamilyForMove}
                        onChange={(e) =>
                          setSelectedFamilyForMove(e.target.value)
                        }
                        className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                        disabled={!selectedCategoryForMove}
                      >
                        <option value="">Select Family</option>
                        {availableFamilies.map((fam) => (
                          <option key={fam} value={fam}>
                            {fam}
                          </option>
                        ))}
                      </select>
                      <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                        Change family to move this product
                      </p>
                    </div>

                    {/* Warning message se categoria/famiglia cambiano */}
                    {(selectedCategoryForMove !== category ||
                      selectedFamilyForMove !== family) && (
                      <div className="ay-bg-yellow-50 ay-border ay-border-yellow-200 ay-rounded-md ay-p-3">
                        <div className="ay-flex">
                          <div className="ay-flex-shrink-0">
                            <svg
                              className="ay-h-5 ay-w-5 ay-text-yellow-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="ay-ml-3">
                            <h3 className="ay-text-sm ay-font-medium ay-text-yellow-800">
                              Product will be moved
                            </h3>
                            <div className="ay-mt-2 ay-text-sm ay-text-yellow-700">
                              <p>
                                This product will be moved from{" "}
                                <strong>
                                  {category}/{family}
                                </strong>{" "}
                                to{" "}
                                <strong>
                                  {selectedCategoryForMove}/
                                  {selectedFamilyForMove}
                                </strong>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="ay-mt-6 ay-w-full ay-pt-4 ay-border-t">
                <h3 className="ay-text-lg ay-font-medium ay-mb-3">
                  Product Translations
                </h3>
                <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-4">
                  <div>
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                      Name (IT)
                    </label>
                    <input
                      type="text"
                      value={formData.lang.it}
                      onChange={(e) => handleLangChange(e, "it")}
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                      placeholder="Nome del prodotto in Italiano"
                    />
                  </div>
                  <div>
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                      Name (EN)
                    </label>
                    <input
                      type="text"
                      value={formData.lang.en}
                      onChange={(e) => handleLangChange(e, "en")}
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                      placeholder="Product name in English"
                    />
                  </div>
                  <div>
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                      Name (FR)
                    </label>
                    <input
                      type="text"
                      value={formData.lang.fr}
                      onChange={(e) => handleLangChange(e, "fr")}
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                      placeholder="Nom du produit en Français"
                    />
                  </div>
                  <div>
                    <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                      Name (DE)
                    </label>
                    <input
                      type="text"
                      value={formData.lang.de}
                      onChange={(e) => handleLangChange(e, "de")}
                      className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md"
                      placeholder="Produktname auf Deutsch"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Material Types View */}
          {view === "types" && (
            <div className="ay-mb-6">
              {/* Inherit from Family Toggle */}
              <div className="ay-flex ay-items-center ay-justify-between ay-mb-4 ay-p-3 ay-bg-gray-50 ay-rounded-md">
                <div>
                  <h3 className="ay-text-md ay-font-medium">
                    Material Settings
                  </h3>
                  <p className="ay-text-sm ay-text-gray-500">
                    Choose whether to inherit material settings from the family
                    or customize them for this product.
                  </p>
                </div>
                <div className="ay-flex ay-items-center ay-gap-2">
                  <span className="ay-text-sm ay-text-gray-700">
                    {inheritFromFamily
                      ? "Inherit from Family"
                      : "Custom Settings"}
                  </span>
                  <label className="ay-relative ay-inline-flex ay-items-center ay-cursor-pointer">
                    <input
                      type="checkbox"
                      className="ay-sr-only ay-peer"
                      checked={!inheritFromFamily}
                      onChange={handleInheritToggle}
                    />
                    <div className="ay-w-11 ay-h-6 ay-bg-gray-200 ay-peer-focus:ay-outline-none ay-peer-focus:ay-ring-4 ay-peer-focus:ay-ring-blue-300 ay-rounded-full ay-peer ay-peer-checked:after:ay-translate-x-full ay-peer-checked:after:ay-border-white after:ay-content-[''] after:ay-absolute after:ay-top-[2px] after:ay-left-[2px] after:ay-bg-white after:ay-border-gray-300 after:ay-border after:ay-rounded-full after:ay-h-5 after:ay-w-5 after:ay-transition-transform ay-peer-checked:ay-bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {!inheritFromFamily && (
                <>
                  <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
                    <h3 className="ay-text-lg ay-font-medium">
                      Material Types
                    </h3>
                    <div className="ay-flex ay-gap-2">
                      <button
                        type="button"
                        onClick={handleAddType}
                        className="ay-px-3 ay-py-1 ay-bg-blue-500 ay-text-white ay-rounded ay-text-sm"
                      >
                        Add Type
                      </button>
                    </div>
                  </div>

                  {/* Types List */}
                  <div className="ay-bg-gray-50 ay-p-3 ay-rounded-md ay-mb-4">
                    {customTypes.length > 0 ? (
                      <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 lg:ay-grid-cols-3 ay-gap-3">
                        {customTypes.map((type, index) => (
                          <div
                            key={index}
                            className={`ay-border ay-rounded-md ay-p-3 ay-cursor-pointer ay-transition-[border-color,box-shadow] ${
                              activeType === type.technicalName
                                ? "ay-border-blue-500 ay-bg-blue-50"
                                : "ay-border-gray-300 ay-hover:ay-border-gray-400"
                            }`}
                            onClick={() => setActiveType(type.technicalName)}
                          >
                            <div className="ay-flex ay-justify-between ay-items-start">
                              <div>
                                <h4 className="ay-font-medium">
                                  {type.displayLabel}
                                </h4>
                                <p className="ay-text-xs ay-text-gray-500">
                                  {type.technicalName}
                                </p>
                                <p className="ay-text-sm ay-mt-1">
                                  {type.materials.length} Materials (
                                  {type.standardMaterials.length} Standard,{" "}
                                  {type.optionalMaterials.length} Optional)
                                </p>
                              </div>
                              <div className="ay-flex ay-flex-col ay-gap-1">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleEditType(index)
                                  }}
                                  className="ay-text-xs ay-text-blue-600 ay-hover:ay-text-blue-800"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDuplicateType(index)
                                  }}
                                  className="ay-text-xs ay-text-green-600 ay-hover:ay-text-green-800"
                                >
                                  Duplicate
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDeleteType(index)
                                  }}
                                  className="ay-text-xs ay-text-red-600 ay-hover:ay-text-red-800"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="ay-text-center ay-py-4 ay-text-gray-500">
                        {inheritFromFamily
                          ? "Using family material types. Switch to custom settings to define product-specific types."
                          : "No material types defined. Add a type to start customizing materials."}
                      </div>
                    )}
                  </div>

                  {/* Active Type Materials Table */}
                  {activeType && !inheritFromFamily && (
                    <div>
                      <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
                        <h4 className="ay-font-medium">
                          {customTypes.find(
                            (t) => t.technicalName === activeType
                          )?.displayLabel || activeType}{" "}
                          Materials
                        </h4>
                        <button
                          type="button"
                          onClick={() => {
                            // Show material selector dialog
                            const typeIndex = customTypes.findIndex(
                              (t) => t.technicalName === activeType
                            )
                            if (typeIndex !== -1) {
                              handleEditType(typeIndex)
                            }
                          }}
                          className="ay-px-3 ay-py-1 ay-bg-green-500 ay-text-white ay-rounded ay-text-sm"
                        >
                          Manage Materials
                        </button>
                      </div>

                      {/* Materials List with Standard/Optional grouping */}
                      <div className="ay-space-y-4">
                        {/* Standard Materials */}
                        <div className="ay-border ay-rounded-md ay-overflow-hidden">
                          <div className="ay-bg-green-50 ay-px-4 ay-py-2 ay-border-b ay-border-green-200">
                            <h5 className="ay-font-medium ay-text-green-800">
                              Standard Materials
                            </h5>
                          </div>
                          <table className="ay-min-w-full ay-divide-y ay-divide-gray-200">
                            <thead className="ay-bg-gray-50">
                              <tr>
                                <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider">
                                  Material
                                </th>
                                <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider">
                                  Preview
                                </th>
                                <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider">
                                  Collection
                                </th>
                              </tr>
                            </thead>
                            <tbody className="ay-bg-white ay-divide-y ay-divide-gray-200">
                              {customTypes
                                .find((t) => t.technicalName === activeType)
                                ?.standardMaterials.map(
                                  (materialName, index) => {
                                    const material =
                                      materialManager.findMaterialByName(
                                        materialName
                                      ) ||
                                      allMaterials.find(
                                        (m) => m.name === materialName
                                      )

                                    return (
                                      <tr key={index}>
                                        <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                                          <div className="ay-text-sm ay-font-medium ay-text-gray-900">
                                            {materialName}
                                          </div>
                                        </td>
                                        <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                                          {material?.thumbnail ? (
                                            material.thumbnail.startsWith(
                                              "#"
                                            ) ? (
                                              <div
                                                className="ay-w-8 ay-h-8 ay-rounded-full ay-border ay-border-gray-300"
                                                style={{
                                                  backgroundColor:
                                                    material.thumbnail,
                                                }}
                                              ></div>
                                            ) : (
                                              <img
                                                src={material.thumbnail}
                                                alt={materialName}
                                                className="ay-w-8 ay-h-8 ay-rounded-full ay-object-cover"
                                              />
                                            )
                                          ) : (
                                            <div className="ay-w-8 ay-h-8 ay-bg-gray-200 ay-rounded-full"></div>
                                          )}
                                        </td>
                                        <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                                          {material?.collection || "Unknown"}
                                        </td>
                                      </tr>
                                    )
                                  }
                                )}
                              {(customTypes.find(
                                (t) => t.technicalName === activeType
                              )?.standardMaterials.length || 0) === 0 && (
                                <tr>
                                  <td
                                    colSpan="3"
                                    className="ay-px-6 ay-py-4 ay-text-center ay-text-gray-500"
                                  >
                                    No standard materials defined for this type.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Optional Materials */}
                        <div className="ay-border ay-rounded-md ay-overflow-hidden">
                          <div className="ay-bg-yellow-50 ay-px-4 ay-py-2 ay-border-b ay-border-yellow-200">
                            <h5 className="ay-font-medium ay-text-yellow-800">
                              Optional Materials (Extra Cost)
                            </h5>
                          </div>
                          <table className="ay-min-w-full ay-divide-y ay-divide-gray-200">
                            <thead className="ay-bg-gray-50">
                              <tr>
                                <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider">
                                  Material
                                </th>
                                <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider">
                                  Preview
                                </th>
                                <th className="ay-px-6 ay-py-3 ay-text-left ay-text-xs ay-font-medium ay-text-gray-500 ay-uppercase ay-tracking-wider">
                                  Collection
                                </th>
                              </tr>
                            </thead>
                            <tbody className="ay-bg-white ay-divide-y ay-divide-gray-200">
                              {customTypes
                                .find((t) => t.technicalName === activeType)
                                ?.optionalMaterials.map(
                                  (materialName, index) => {
                                    const material =
                                      materialManager.findMaterialByName(
                                        materialName
                                      ) ||
                                      allMaterials.find(
                                        (m) => m.name === materialName
                                      )

                                    return (
                                      <tr key={index}>
                                        <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                                          <div className="ay-text-sm ay-font-medium ay-text-gray-900">
                                            {materialName}
                                          </div>
                                        </td>
                                        <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                                          {material?.thumbnail ? (
                                            material.thumbnail.startsWith(
                                              "#"
                                            ) ? (
                                              <div
                                                className="ay-w-8 ay-h-8 ay-rounded-full ay-border ay-border-gray-300"
                                                style={{
                                                  backgroundColor:
                                                    material.thumbnail,
                                                }}
                                              ></div>
                                            ) : (
                                              <img
                                                src={material.thumbnail}
                                                alt={materialName}
                                                className="ay-w-8 ay-h-8 ay-rounded-full ay-object-cover"
                                              />
                                            )
                                          ) : (
                                            <div className="ay-w-8 ay-h-8 ay-bg-gray-200 ay-rounded-full"></div>
                                          )}
                                        </td>
                                        <td className="ay-px-6 ay-py-4 ay-whitespace-nowrap">
                                          {material?.collection || "Unknown"}
                                        </td>
                                      </tr>
                                    )
                                  }
                                )}
                              {(customTypes.find(
                                (t) => t.technicalName === activeType
                              )?.optionalMaterials.length || 0) === 0 && (
                                <tr>
                                  <td
                                    colSpan="3"
                                    className="ay-px-6 ay-py-4 ay-text-center ay-text-gray-500"
                                  >
                                    No optional materials defined for this type.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {inheritFromFamily && (
                <div className="ay-bg-blue-50 ay-p-4 ay-rounded-md ay-text-blue-800 ay-text-sm">
                  <h4 className="ay-font-medium ay-mb-2">
                    Using Family Default Settings
                  </h4>
                  <p>
                    This product will inherit material types and options from
                    the family defaults. Toggle the switch above to customize
                    materials for this specific product.
                  </p>

                  <div className="ay-mt-4 ay-border-t ay-border-blue-200 ay-pt-3">
                    <h5 className="ay-font-medium ay-mb-2">
                      Family Materials Summary:
                    </h5>
                    <ul className="ay-list-disc ay-pl-5 ay-space-y-1">
                      {Object.entries(familyDefaults).map(
                        ([techName, config]) => (
                          <li key={techName}>
                            <span className="ay-font-medium">
                              {config.label || techName}:
                            </span>{" "}
                            <span className="ay-text-sm">
                              {config.standard?.length || 0} standard,{" "}
                              {config.optional?.length || 0} optional materials
                            </span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Type Links View */}
          {view === "typeLinks" && !inheritFromFamily && (
            <div>
              <h3 className="ay-text-lg ay-font-medium ay-mb-4">Type Links</h3>
              <TypeLinksComponent
                customTypes={customTypes}
                initialLinks={formData.typeLinks}
                onChange={(typeLinks) => {
                  // Handle type links changes
                  setFormData((prev) => ({
                    ...prev,
                    typeLinks,
                  }))
                }}
                setLoading={setLoading}
              />
            </div>
          )}

          {/* Type Links View - Inherit from Family */}
          {view === "typeLinks" && inheritFromFamily && (
            <div className="ay-bg-blue-50 ay-p-4 ay-rounded-md ay-text-blue-800 ay-text-sm">
              <h4 className="ay-font-medium ay-mb-2">
                Using Family Type Links
              </h4>
              <p>
                This product will inherit type links from the family defaults.
                To customize type links for this specific product, switch to
                custom material settings in the Material Types tab.
              </p>
            </div>
          )}

          {/* Info Section View */}
          {view === "infoSection" && (
            <div>
              {/* Usage Images Section */}
              <div className="ay-mb-8">
                <h3 className="ay-text-lg ay-font-medium ay-mb-4">
                  Usage Images (Come potresti utilizzare le immagini)
                </h3>
                <p className="ay-text-sm ay-text-gray-500 ay-mb-4">
                  Add up to 4 images showing how this product can be used. These will be displayed in the Info section below the 3D viewer.
                </p>
                <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 lg:ay-grid-cols-4 ay-gap-4">
                  {[0, 1, 2, 3].map((index) => (
                    <div key={index} className="ay-border ay-rounded-md ay-p-3">
                      <label className="ay-block ay-text-sm ay-font-medium ay-mb-2">
                        Image {index + 1}
                      </label>
                      <input
                        type="text"
                        value={formData.usageImages?.[index] || ""}
                        onChange={(e) => handleUsageImageChange(index, e.target.value)}
                        placeholder="URL immagine"
                        className="ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                      />
                      {formData.usageImages?.[index] && (
                        <img
                          src={formData.usageImages[index]}
                          alt={`Usage ${index + 1}`}
                          className="ay-mt-2 ay-w-full ay-h-32 ay-object-cover ay-rounded ay-bg-gray-100"
                          onError={(e) => {
                            e.target.style.display = "none"
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Related Products Section */}
              <div>
                <h3 className="ay-text-lg ay-font-medium ay-mb-4">
                  Related Products (Potrebbe piacerti anche)
                </h3>
                <p className="ay-text-sm ay-text-gray-500 ay-mb-4">
                  Select up to 4 related products. For each product, you can configure which materials to display.
                </p>
                <div className="ay-space-y-6">
                  {[0, 1, 2, 3].map((index) => {
                    const relatedProduct = formData.relatedProducts?.[index]
                    const selectedProduct = relatedProduct
                      ? allProducts.find((p) => p.code === relatedProduct.code)
                      : null

                    // Debug log
                    if (selectedProduct) {
                      console.log("Selected product:", selectedProduct.name)
                      console.log("Product _family:", selectedProduct._family)
                      console.log("Product _familyDefaults:", selectedProduct._familyDefaults)
                    }

                    return (
                      <div
                        key={index}
                        className="ay-border ay-rounded-md ay-p-4 ay-bg-gray-50"
                      >
                        <div className="ay-flex ay-items-center ay-gap-4 ay-mb-4">
                          <label className="ay-text-sm ay-font-medium ay-whitespace-nowrap">
                            Product {index + 1}:
                          </label>
                          <select
                            value={relatedProduct?.code || ""}
                            onChange={(e) =>
                              handleRelatedProductChange(index, e.target.value)
                            }
                            className="ay-flex-1 ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                          >
                            <option value="">-- Select a product --</option>
                            {allProducts
                              .filter((p) => p.code !== formData.code)
                              .map((p) => (
                                <option key={p.code} value={p.code}>
                                  {p.name} ({p.code})
                                </option>
                              ))}
                          </select>
                        </div>

                        {/* Image URL for this related product */}
                        {relatedProduct?.code && (
                          <div className="ay-mb-3">
                            <label className="ay-block ay-text-xs ay-font-medium ay-text-gray-600 ay-mb-1">
                              Preview Image URL:
                            </label>
                            <input
                              type="text"
                              value={relatedProduct?.imageUrl || ""}
                              onChange={(e) =>
                                handleRelatedProductImageChange(index, e.target.value)
                              }
                              placeholder="https://example.com/image.jpg"
                              className="ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                            />
                            {relatedProduct?.imageUrl && (
                              <img
                                src={relatedProduct.imageUrl}
                                alt="Preview"
                                className="ay-mt-2 ay-h-20 ay-object-contain ay-bg-gray-100 ay-rounded"
                                onError={(e) => {
                                  e.target.style.display = "none"
                                }}
                              />
                            )}
                          </div>
                        )}

                        {/* Material configuration for selected product */}
                        {selectedProduct && (
                          <div className="ay-mt-3 ay-pt-3 ay-border-t ay-border-gray-200">
                            <p className="ay-text-xs ay-text-gray-500 ay-mb-2">
                              Configure materials for this product:
                            </p>
                            <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-4 ay-gap-3">
                              {/* Get material types from the selected product's family or product materials */}
                              {(() => {
                                const materialTypes = selectedProduct._familyDefaults?.defaultMaterials
                                  ? Object.keys(selectedProduct._familyDefaults.defaultMaterials)
                                  : selectedProduct.materials
                                  ? Object.keys(selectedProduct.materials)
                                  : []

                                return materialTypes.map((matType) => {
                                  // Get available materials for this type
                                  const typeConfig =
                                    selectedProduct._familyDefaults?.defaultMaterials?.[matType] ||
                                    selectedProduct.materials?.[matType]
                                  const availableSlugs = [
                                    ...(typeConfig?.standard || []),
                                    ...(typeConfig?.optional || []),
                                  ]

                                  return (
                                    <div key={matType}>
                                      <label className="ay-block ay-text-xs ay-font-medium ay-text-gray-600 ay-mb-1">
                                        {matType}
                                      </label>
                                      <select
                                        value={relatedProduct?.materials?.[matType] || ""}
                                        onChange={(e) =>
                                          handleRelatedProductMaterialChange(
                                            index,
                                            matType,
                                            e.target.value
                                          )
                                        }
                                        className="ay-w-full ay-px-2 ay-py-1 ay-border ay-border-gray-300 ay-rounded ay-text-xs"
                                      >
                                        <option value="">Default</option>
                                        {availableSlugs.map((slug) => {
                                          const mat = materialManager.findMaterialBySlug(slug)
                                          return (
                                            <option key={slug} value={slug}>
                                              {mat?.name || slug}
                                            </option>
                                          )
                                        })}
                                      </select>
                                    </div>
                                  )
                                })
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Type Form Modal */}
        {showTypeForm && (
          <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
            <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-5xl ay-max-h-screen ay-overflow-auto">
              <h3 className="ay-text-lg ay-font-bold ay-mb-4">
                {editingTypeIndex !== null ? "Edit" : "Add"} Material Type
              </h3>

              <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-2 ay-gap-4 ay-mb-4">
                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Technical Name *
                  </label>
                  <input
                    type="text"
                    name="technicalName"
                    value={typeFormData.technicalName}
                    onChange={handleTypeFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    required
                  />
                  <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                    Must match the technical name used in the 3D model.
                  </p>
                </div>

                <div>
                  <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                    Display Label *
                  </label>
                  <input
                    type="text"
                    name="displayLabel"
                    value={typeFormData.displayLabel}
                    onChange={handleTypeFormChange}
                    className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                    required
                  />
                  <p className="ay-text-xs ay-text-gray-500 ay-mt-1">
                    This label will be displayed in the accordion.
                  </p>
                </div>
              </div>

              {/* Material Selection */}
              <div className="ay-mb-6">
                <div className="ay-flex ay-justify-between ay-items-center ay-mb-2">
                  <h4 className="ay-text-md ay-font-medium">
                    Material Selection
                  </h4>
                  <div className="ay-flex ay-items-center ay-gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        // Sort materials alphabetically
                        setAllMaterials(
                          [...allMaterials].sort((a, b) =>
                            a.name.localeCompare(b.name)
                          )
                        )
                      }}
                      className="ay-px-2 ay-py-1 ay-bg-gray-200 ay-text-gray-700 ay-rounded ay-text-xs"
                    >
                      Sort A-Z
                    </button>
                  </div>
                </div>

                {/* Material Selection Controls */}
                <div className="ay-flex ay-justify-between ay-mb-3 ay-bg-gray-50 ay-p-2 ay-rounded">
                  <div className="ay-flex ay-items-center ay-gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        // Select all materials as optional (default)
                        const allMaterialNames = allMaterials.map((m) => m.name)
                        handleBulkAddMaterials(allMaterialNames, false, true)
                      }}
                      className="ay-px-2 ay-py-1 ay-bg-yellow-400 ay-text-white ay-rounded ay-text-xs"
                    >
                      Select All as Optional
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // Select all materials as standard
                        const allMaterialNames = allMaterials.map((m) => m.name)
                        handleBulkAddMaterials(allMaterialNames, true, false)
                      }}
                      className="ay-px-2 ay-py-1 ay-bg-green-500 ay-text-white ay-rounded ay-text-xs"
                    >
                      Select All as Standard
                    </button>
                  </div>
                  <span className="ay-text-xs ay-text-gray-500">
                    Click = Optional | Shift+Click = Standard
                  </span>
                </div>

                {/* Collections Grid */}
                <div className="ay-mb-4">
                  <h5 className="ay-text-sm ay-font-medium ay-mb-2">
                    Collections
                  </h5>
                  <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 lg:ay-grid-cols-4 ay-gap-2">
                    {getUniqueCollections()
                      .sort()
                      .map((collection) => (
                        <div
                          key={collection}
                          className="ay-border ay-border-gray-200 ay-rounded-md ay-p-2 ay-cursor-pointer ay-hover:ay-bg-gray-50"
                          onClick={() => {
                            const materialsToAdd = getMaterialsByCollection(
                              collection
                            ).map((m) => m.name)
                            handleBulkAddMaterials(materialsToAdd, false, true) // Default to optional
                          }}
                        >
                          <div className="ay-flex ay-justify-between ay-items-center">
                            <span className="ay-text-sm">{collection}</span>
                            <div className="ay-flex ay-gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const materialsToAdd =
                                    getMaterialsByCollection(collection).map(
                                      (m) => m.name
                                    )
                                  handleBulkAddMaterials(
                                    materialsToAdd,
                                    true,
                                    false
                                  )
                                }}
                                className="ay-px-1 ay-text-xs ay-bg-green-500 ay-text-white ay-rounded"
                              >
                                STD
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const materialsToAdd =
                                    getMaterialsByCollection(collection).map(
                                      (m) => m.name
                                    )
                                  handleBulkAddMaterials(
                                    materialsToAdd,
                                    false,
                                    true
                                  )
                                }}
                                className="ay-px-1 ay-text-xs ay-bg-yellow-500 ay-text-white ay-rounded"
                              >
                                OPT
                              </button>
                            </div>
                          </div>
                          <div className="ay-text-xs ay-text-gray-500 ay-mt-1">
                            {getMaterialsByCollection(collection).length}{" "}
                            materials
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Material Selection Grid */}
                <div className="ay-border ay-border-gray-200 ay-rounded-md ay-p-3 ay-mb-4">
                  <h5 className="ay-text-sm ay-font-medium ay-mb-2">
                    All Materials
                  </h5>

                  <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 lg:ay-grid-cols-4 ay-gap-2 ay-max-h-64 ay-overflow-y-auto ay-p-2">
                    {allMaterials
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((material) => {
                        const isSelected = typeFormData.materials.includes(
                          material.name
                        )
                        const isStandard =
                          typeFormData.standardMaterials.includes(material.name)
                        const isOptional =
                          typeFormData.optionalMaterials.includes(material.name)

                        let statusClass = ""
                        if (isStandard)
                          statusClass = "ay-border-green-500 ay-bg-green-50"
                        else if (isOptional)
                          statusClass = "ay-border-yellow-500 ay-bg-yellow-50"

                        return (
                          <div
                            key={material.id || material.name}
                            className={`ay-border ay-rounded-md ay-p-2 ay-cursor-pointer ${statusClass} ay-hover:ay-shadow`}
                            onClick={(e) => {
                              // Handle click with Shift key for Standard, otherwise Optional
                              if (e.shiftKey) {
                                handleMaterialStatusChange(
                                  material.name,
                                  isStandard ? "none" : "standard"
                                )
                              } else {
                                handleMaterialStatusChange(
                                  material.name,
                                  isOptional ? "none" : "optional"
                                )
                              }
                            }}
                          >
                            <div className="ay-flex ay-items-center ay-gap-2">
                              {material.thumbnail ? (
                                material.thumbnail.startsWith("#") ? (
                                  <div
                                    className="ay-w-6 ay-h-6 ay-rounded-full ay-border ay-border-gray-300"
                                    style={{
                                      backgroundColor: material.thumbnail,
                                    }}
                                  ></div>
                                ) : (
                                  <img
                                    src={material.thumbnail}
                                    alt={material.name}
                                    className="ay-w-6 ay-h-6 ay-rounded-full ay-object-cover"
                                  />
                                )
                              ) : (
                                <div className="ay-w-6 ay-h-6 ay-bg-gray-200 ay-rounded-full"></div>
                              )}
                              <div className="ay-flex-1 ay-overflow-hidden">
                                <div className="ay-text-sm ay-truncate">
                                  {material.name}
                                </div>
                                <div className="ay-text-xs ay-text-gray-500">
                                  {material.collection || "Unknown"}
                                </div>
                              </div>

                              {/* Status indicator */}
                              {isSelected && (
                                <div className="ay-flex ay-flex-col ay-gap-1">
                                  {isStandard && (
                                    <span className="ay-px-1 ay-bg-green-500 ay-text-white ay-text-xs ay-rounded">
                                      STD
                                    </span>
                                  )}
                                  {isOptional && (
                                    <span className="ay-px-1 ay-bg-yellow-400 ay-text-white ay-text-xs ay-rounded">
                                      OPT
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleRemoveMaterial(material.name)
                                    }}
                                    className="ay-px-1 ay-text-xs ay-bg-red-500 ay-text-white ay-rounded"
                                  >
                                    ✕
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>

                {/* Selected Materials Summary */}
                <div className="ay-border ay-border-gray-200 ay-rounded-md ay-p-3">
                  <h5 className="ay-text-sm ay-font-medium ay-mb-2">
                    Selected Materials
                  </h5>

                  <div className="ay-mb-3">
                    <h6 className="ay-text-xs ay-font-medium ay-bg-green-50 ay-text-green-800 ay-p-1 ay-pl-2">
                      Standard Materials (
                      {typeFormData.standardMaterials.length})
                    </h6>
                    <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 lg:ay-grid-cols-4 ay-gap-2 ay-mt-2">
                      {typeFormData.standardMaterials.length > 0 ? (
                        typeFormData.standardMaterials.map((materialName) => {
                          const material = allMaterials.find(
                            (m) => m.name === materialName
                          )
                          return (
                            <div
                              key={materialName}
                              className="ay-border ay-rounded-md ay-p-2 ay-bg-green-50 ay-border-green-200"
                            >
                              <div className="ay-flex ay-items-center ay-gap-2">
                                {material?.thumbnail ? (
                                  material.thumbnail.startsWith("#") ? (
                                    <div
                                      className="ay-w-6 ay-h-6 ay-rounded-full ay-border ay-border-gray-300"
                                      style={{
                                        backgroundColor: material.thumbnail,
                                      }}
                                    ></div>
                                  ) : (
                                    <img
                                      src={material.thumbnail}
                                      alt={materialName}
                                      className="ay-w-6 ay-h-6 ay-rounded-full ay-object-cover"
                                    />
                                  )
                                ) : (
                                  <div className="ay-w-6 ay-h-6 ay-bg-gray-200 ay-rounded-full"></div>
                                )}
                                <div className="ay-flex-1 ay-overflow-hidden">
                                  <div className="ay-text-sm ay-truncate">
                                    {materialName}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleMaterialStatusChange(
                                      materialName,
                                      "none"
                                    )
                                  }
                                  className="ay-px-1 ay-py-0.5 ay-text-xs ay-bg-red-500 ay-text-white ay-rounded"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          )
                        })
                      ) : (
                        <div className="ay-col-span-full ay-text-center ay-py-2 ay-text-gray-500 ay-text-xs">
                          No standard materials selected
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h6 className="ay-text-xs ay-font-medium ay-bg-yellow-50 ay-text-yellow-800 ay-p-1 ay-pl-2">
                      Optional Materials (
                      {typeFormData.optionalMaterials.length})
                    </h6>
                    <div className="ay-grid ay-grid-cols-2 md:ay-grid-cols-3 lg:ay-grid-cols-4 ay-gap-2 ay-mt-2">
                      {typeFormData.optionalMaterials.length > 0 ? (
                        typeFormData.optionalMaterials.map((materialName) => {
                          const material = allMaterials.find(
                            (m) => m.name === materialName
                          )
                          return (
                            <div
                              key={materialName}
                              className="ay-border ay-rounded-md ay-p-2 ay-bg-yellow-50 ay-border-yellow-200"
                            >
                              <div className="ay-flex ay-items-center ay-gap-2">
                                {material?.thumbnail ? (
                                  material.thumbnail.startsWith("#") ? (
                                    <div
                                      className="ay-w-6 ay-h-6 ay-rounded-full ay-border ay-border-gray-300"
                                      style={{
                                        backgroundColor: material.thumbnail,
                                      }}
                                    ></div>
                                  ) : (
                                    <img
                                      src={material.thumbnail}
                                      alt={materialName}
                                      className="ay-w-6 ay-h-6 ay-rounded-full ay-object-cover"
                                    />
                                  )
                                ) : (
                                  <div className="ay-w-6 ay-h-6 ay-bg-gray-200 ay-rounded-full"></div>
                                )}
                                <div className="ay-flex-1 ay-overflow-hidden">
                                  <div className="ay-text-sm ay-truncate">
                                    {materialName}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleMaterialStatusChange(
                                      materialName,
                                      "none"
                                    )
                                  }
                                  className="ay-px-1 ay-py-0.5 ay-text-xs ay-bg-red-500 ay-text-white ay-rounded"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          )
                        })
                      ) : (
                        <div className="ay-col-span-full ay-text-center ay-py-2 ay-text-gray-500 ay-text-xs">
                          No optional materials selected
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="ay-flex ay-justify-end ay-gap-3 ay-mt-6">
                <button
                  type="button"
                  onClick={() => setShowTypeForm(false)}
                  className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveType}
                  className="ay-px-4 ay-py-2 ay-border ay-border-transparent ay-rounded-md ay-shadow-sm ay-bg-blue-500 ay-text-white ay-hover:ay-bg-blue-600"
                >
                  {editingTypeIndex !== null ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductForm
